/**
 * BugReportsTab.jsx
 * 
 * Platform Admin tab for managing CCM bug reports.
 * Shows all captured bugs across tenants with status management.
 * 
 * Place in: /src/components/BugReportsTab.jsx
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Bug, AlertTriangle, CheckCircle, Clock, ChevronDown, ChevronRight,
  XCircle, Eye, Loader2, RefreshCw, Search, Filter, Image, User, Link, FileText
} from 'lucide-react';
import { toast } from 'react-toastify';

const STATUS_CONFIG = {
  OPEN: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)', label: 'Open', icon: XCircle },
  ACKNOWLEDGED: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'Acknowledged', icon: Eye },
  RESOLVED: { color: '#22c55e', bg: 'rgba(34,197,94,0.12)', label: 'Resolved', icon: CheckCircle },
};

const formatDate = (d) => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatRelativeTime = (date) => {
  if (!date) return 'Never';
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const BugReportsTab = () => {
  const [bugs, setBugs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBug, setExpandedBug] = useState(null);
  const [stats, setStats] = useState({ open: 0, acknowledged: 0, resolved: 0, total: 0 });
  const [updatingId, setUpdatingId] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [showNotesFor, setShowNotesFor] = useState(null);

  const fetchBugs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('limit', '50');

      const res = await fetch(`/api/platform/bugs?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBugs(data.bugs || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch bugs:', err);
      toast.error('Failed to load bug reports');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch('/api/platform/bugs/stats', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch bug stats:', err);
    }
  }, []);

  useEffect(() => { fetchBugs(); }, [fetchBugs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const updateBugStatus = async (bugId, newStatus, notes) => {
    setUpdatingId(bugId);
    try {
      const res = await fetch(`/api/platform/bugs/${bugId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus, notes: notes || undefined }),
      });
      if (res.ok) {
        toast.success(`Bug ${newStatus.toLowerCase()}`);
        setActionNotes('');
        setShowNotesFor(null);
        fetchBugs();
        fetchStats();
      } else {
        toast.error('Failed to update bug');
      }
    } catch {
      toast.error('Failed to update bug');
    } finally {
      setUpdatingId(null);
    }
  };

  // Filter by search term
  const filteredBugs = bugs.filter(b => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      b.simple?.toLowerCase().includes(s) ||
      b.bugCode?.toLowerCase().includes(s) ||
      b.deviceName?.toLowerCase().includes(s) ||
      b.deviceIp?.toLowerCase().includes(s) ||
      b.category?.toLowerCase().includes(s) ||
      b.tenant?.name?.toLowerCase().includes(s) ||
      b.reportedByEmail?.toLowerCase().includes(s) ||
      b.description?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="bug-reports-tab">
      {/* Stats Row */}
      <div className="bug-stats-row">
        <div
          className={`bug-stat-pill ${!statusFilter ? 'active' : ''}`}
          onClick={() => setStatusFilter('')}
        >
          <Bug size={14} /> All ({stats.total})
        </div>
        <div
          className={`bug-stat-pill open ${statusFilter === 'OPEN' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'OPEN' ? '' : 'OPEN')}
        >
          <XCircle size={14} /> Open ({stats.open})
        </div>
        <div
          className={`bug-stat-pill acknowledged ${statusFilter === 'ACKNOWLEDGED' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'ACKNOWLEDGED' ? '' : 'ACKNOWLEDGED')}
        >
          <Eye size={14} /> Acknowledged ({stats.acknowledged})
        </div>
        <div
          className={`bug-stat-pill resolved ${statusFilter === 'RESOLVED' ? 'active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'RESOLVED' ? '' : 'RESOLVED')}
        >
          <CheckCircle size={14} /> Resolved ({stats.resolved})
        </div>
      </div>

      {/* Search + Refresh */}
      <div className="bug-toolbar">
        <div className="bug-search">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search by bug code, device, category, tenant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => { fetchBugs(); fetchStats(); }} disabled={loading}>
          <RefreshCw size={14} className={loading ? 'spin' : ''} /> Refresh
        </button>
      </div>

      {/* Bug List */}
      {loading ? (
        <div className="bug-loading">
          <Loader2 size={24} className="spin" />
          <span>Loading bug reports...</span>
        </div>
      ) : filteredBugs.length === 0 ? (
        <div className="bug-empty">
          <Bug size={32} />
          <h3>{bugs.length === 0 ? 'No bug reports' : 'No bugs match your search'}</h3>
          <p>{bugs.length === 0 ? 'CCM bugs will appear here when detected.' : 'Try adjusting your filters.'}</p>
        </div>
      ) : (
        <div className="bug-list">
          {filteredBugs.map(bug => {
            const statusCfg = STATUS_CONFIG[bug.status] || STATUS_CONFIG.OPEN;
            const StatusIcon = statusCfg.icon;
            const isExpanded = expandedBug === bug.id;

            return (
              <div key={bug.id} className={`bug-card ${bug.status?.toLowerCase()}`}>
                <div className="bug-card-header" onClick={() => setExpandedBug(isExpanded ? null : bug.id)}>
                  <div className="bug-card-left">
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <span className="bug-status-badge" style={{ background: statusCfg.bg, color: statusCfg.color }}>
                      <StatusIcon size={12} /> {statusCfg.label}
                    </span>
                    <code className="bug-code">{bug.bugCode}</code>
                    {bug.reportType === 'USER_SUBMITTED' && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(59,130,246,0.12)', color: '#60a5fa' }}>User Report</span>}
                    <span className="bug-simple">{bug.simple}</span>
                  </div>
                  <div className="bug-card-right">
                    <span className="bug-category">{bug.category}</span>
                    <span className="bug-occurrences">{bug.occurrences}x</span>
                    <span className="bug-time">{formatRelativeTime(bug.lastSeenAt)}</span>
                  </div>
                </div>

                {isExpanded && (
                  <div className="bug-card-detail">
                    <div className="bug-detail-grid">
                      <div className="bug-detail-item">
                        <label>Device</label>
                        <span>{bug.deviceName || 'N/A'} ({bug.deviceIp || 'N/A'})</span>
                      </div>
                      <div className="bug-detail-item">
                        <label>Device Type</label>
                        <span>{bug.deviceType}</span>
                      </div>
                      <div className="bug-detail-item">
                        <label>Tenant</label>
                        <span>{bug.tenant?.name || bug.tenantId}</span>
                      </div>
                      <div className="bug-detail-item">
                        <label>First Seen</label>
                        <span>{formatDate(bug.firstSeenAt)}</span>
                      </div>
                      <div className="bug-detail-item">
                        <label>Last Seen</label>
                        <span>{formatDate(bug.lastSeenAt)}</span>
                      </div>
                      <div className="bug-detail-item">
                        <label>Occurrences</label>
                        <span>{bug.occurrences}</span>
                      </div>
                    </div>

                    {/* User-submitted bug info */}
                    {bug.reportType === 'USER_SUBMITTED' && (
                      <div className="bug-user-submitted-banner" style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                        background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)',
                        borderRadius: 8, marginBottom: 12, fontSize: 12, color: '#60a5fa'
                      }}>
                        <User size={14} />
                        <span>User-submitted bug report</span>
                        {bug.reportedByEmail && <span style={{ color: 'rgba(255,255,255,0.5)' }}>by {bug.reportedByEmail}</span>}
                        {bug.pageUrl && <span style={{ color: 'rgba(255,255,255,0.35)' }}>on {bug.pageUrl}</span>}
                      </div>
                    )}

                    {bug.description && (
                      <div className="bug-detail-section">
                        <label>User Description</label>
                        <p>{bug.description}</p>
                      </div>
                    )}

                    {bug.screenshotUrl && (
                      <div className="bug-detail-section">
                        <label><Image size={12} /> Screenshot</label>
                        <div style={{ marginTop: 6 }}>
                          <a href={bug.screenshotUrl} target="_blank" rel="noopener noreferrer"
                            style={{ display: 'inline-block', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, overflow: 'hidden' }}>
                            <img src={bug.screenshotUrl} alt="Bug screenshot"
                              style={{ maxWidth: '100%', maxHeight: 300, display: 'block' }}
                              onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<span style="padding: 12px; color: rgba(255,255,255,0.4); font-size: 12px;">Screenshot failed to load</span>'; }}
                            />
                          </a>
                        </div>
                      </div>
                    )}

                    {bug.detail && bug.reportType !== 'USER_SUBMITTED' && (
                      <div className="bug-detail-section">
                        <label>Description</label>
                        <p>{bug.detail}</p>
                      </div>
                    )}

                    <div className="bug-detail-section">
                      <label>Raw Error</label>
                      <code className="bug-raw-error">{bug.raw}</code>
                    </div>

                    {bug.notes && (
                      <div className="bug-detail-section">
                        <label>Notes</label>
                        <p>{bug.notes}</p>
                      </div>
                    )}

                    {bug.resolvedBy && (
                      <div className="bug-detail-section">
                        <label>Resolved By</label>
                        <span>{bug.resolvedBy} on {formatDate(bug.resolvedAt)}</span>
                      </div>
                    )}

                    <div className="bug-actions" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {showNotesFor === bug.id && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                          <textarea
                            value={actionNotes}
                            onChange={e => setActionNotes(e.target.value)}
                            placeholder="Add notes for the reporter (optional)..."
                            style={{
                              flex: 1, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
                              borderRadius: 8, color: 'var(--text-primary)', fontSize: 12, padding: '8px 10px',
                              minHeight: 60, resize: 'vertical', fontFamily: 'inherit', outline: 'none',
                            }}
                          />
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 8 }}>
                        {bug.status === 'OPEN' && (
                          showNotesFor === bug.id ? (
                            <>
                              <button
                                className="btn btn-sm"
                                style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}
                                onClick={() => updateBugStatus(bug.id, 'ACKNOWLEDGED', actionNotes)}
                                disabled={updatingId === bug.id}
                              >
                                {updatingId === bug.id ? <Loader2 size={12} className="spin" /> : <Eye size={12} />} Confirm Acknowledge
                              </button>
                              <button className="btn btn-sm btn-secondary" onClick={() => { setShowNotesFor(null); setActionNotes(''); }}>Cancel</button>
                            </>
                          ) : (
                            <button
                              className="btn btn-sm"
                              style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}
                              onClick={() => setShowNotesFor(bug.id)}
                              disabled={updatingId === bug.id}
                            >
                              <Eye size={12} /> Acknowledge
                            </button>
                          )
                        )}
                        {bug.status !== 'RESOLVED' && (
                          showNotesFor === bug.id && bug.status !== 'OPEN' ? (
                            <>
                              <button
                                className="btn btn-sm"
                                style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }}
                                onClick={() => updateBugStatus(bug.id, 'RESOLVED', actionNotes)}
                                disabled={updatingId === bug.id}
                              >
                                {updatingId === bug.id ? <Loader2 size={12} className="spin" /> : <CheckCircle size={12} />} Confirm Resolve
                              </button>
                              <button className="btn btn-sm btn-secondary" onClick={() => { setShowNotesFor(null); setActionNotes(''); }}>Cancel</button>
                            </>
                          ) : bug.status !== 'OPEN' || showNotesFor !== bug.id ? (
                            <button
                              className="btn btn-sm"
                              style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }}
                              onClick={() => setShowNotesFor(bug.id)}
                              disabled={updatingId === bug.id}
                            >
                              <CheckCircle size={12} /> Mark Resolved
                            </button>
                          ) : null
                        )}
                        {bug.status === 'RESOLVED' && (
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => updateBugStatus(bug.id, 'OPEN')}
                            disabled={updatingId === bug.id}
                          >
                            Reopen
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && filteredBugs.length > 0 && (
        <div className="bug-footer">
          Showing {filteredBugs.length} of {total} bug report{total !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
};

export default BugReportsTab;
